import React, { useState, useEffect } from 'react';
import moment from 'moment';

const headerStyle = {
    border: '1px solid #e5e7eb',
    padding: '10px 14px',
    textAlign: 'left',
    backgroundColor: '#f9fafb',
    fontWeight: 600,
    fontSize: '13px',
    color: '#374151',
};

const cellStyle = {
    border: '1px solid #e5e7eb',
    padding: '10px 14px',
    textAlign: 'left',
    fontSize: '13px',
    color: '#1f2937',
};

const tableStyle = {
    border: '1px solid #e5e7eb',
    borderCollapse: 'collapse',
    width: '100%',
    tableLayout: 'fixed',
};

const columnWidthsWithoutAction = ['10%', '10%', '7%', '8%', '9%', '8%', '8%', '6%', '6%', '14%', '14%'];
const columnWidthsWithAction = ['9%', '9%', '6%', '7%', '8%', '7%', '7%', '6%', '6%', '12%', '12%', '8%'];

const modalOverlayStyle = {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
};

const modalContentStyle = {
    backgroundColor: '#fff',
    borderRadius: '8px',
    padding: '24px',
    minWidth: '320px',
    maxWidth: '90vw',
    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1)',
};

const subTitleStyle = {
    fontSize: '13px',
    fontWeight: 500,
    color: '#6b7280',
    marginBottom: '6px',
    marginTop: '14px',
};

const formatTime = (isoString) => {
    if (!isoString) return '-';
    return moment(isoString).format('HH:mm');
};

const isNoDataRow = (row) =>
    row.passReason === 'No data' || row.passReason === 'Invalid times';

/**
 * Modal to set Pass or Fail override for a booking (similar to Inbound SLA manual override).
 */
const PassFailOverrideModal = ({ row, open, onClose, onApply, isPatching }) => {
    const [selectedPass, setSelectedPass] = useState(true);
    const [reason, setReason] = useState('');

    useEffect(() => {
        if (open) {
            setSelectedPass(true);
            setReason('');
        }
    }, [open]);

    if (!open) return null;

    const trimmedReason = reason?.trim();
    const canApply = !!trimmedReason;

    const handleApply = () => {
        if (row && onApply && trimmedReason) onApply(row, selectedPass, trimmedReason);
    };

    return (
        <div style={modalOverlayStyle} onClick={isPatching ? undefined : onClose} role="dialog" aria-modal="true" aria-labelledby="pass-fail-override-title">
            <div style={modalContentStyle} onClick={(e) => e.stopPropagation()}>
                <h2 id="pass-fail-override-title" style={{ margin: '0 0 16px', fontSize: '18px', fontWeight: 600, color: '#1f2937' }}>
                    Terminal Reception Override
                </h2>
                <p style={{ margin: '0 0 16px', fontSize: '14px', color: '#6b7280' }}>
                    Manually set pass or fail for {row?.passengerName || 'this passenger'}. This will override the calculated result.
                </p>
                <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', cursor: 'pointer', fontSize: '14px' }}>
                        <input type="radio" name="tr-override" checked={selectedPass} onChange={() => setSelectedPass(true)} />
                        Pass (Manual)
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px' }}>
                        <input type="radio" name="tr-override" checked={!selectedPass} onChange={() => setSelectedPass(false)} />
                        Fail (Manual)
                    </label>
                    <div style={{ marginTop: '16px' }}>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 500, color: '#374151' }}>
                            Reason (required)
                        </label>
                        <input
                            type="text"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder={selectedPass ? 'e.g. Agent was present before passenger arrived' : 'e.g. No agent at reception desk'}
                            style={{
                                width: '100%',
                                padding: '8px 12px',
                                fontSize: '14px',
                                border: '1px solid #d1d5db',
                                borderRadius: '6px',
                                boxSizing: 'border-box',
                            }}
                        />
                    </div>
                </div>
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', alignItems: 'center' }}>
                    {isPatching && (
                        <span style={{ marginRight: '8px', fontSize: '13px', color: '#6b7280' }}>Updating...</span>
                    )}
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={isPatching}
                        style={{
                            padding: '8px 16px',
                            fontSize: '13px',
                            cursor: isPatching ? 'not-allowed' : 'pointer',
                            backgroundColor: '#f3f4f6',
                            color: '#374151',
                            border: '1px solid #e5e7eb',
                            borderRadius: '6px',
                            opacity: isPatching ? 0.6 : 1,
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleApply}
                        disabled={isPatching || !canApply}
                        style={{
                            padding: '8px 16px',
                            fontSize: '13px',
                            cursor: (isPatching || !canApply) ? 'not-allowed' : 'pointer',
                            backgroundColor: '#3b82f6',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '6px',
                            opacity: (isPatching || !canApply) ? 0.6 : 1,
                        }}
                    >
                        {isPatching ? 'Updating...' : 'Apply'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const KpiRawDataSection = ({
    title, minutes, rawData, date, kpiKey,
    timeColumnKey = 'arrivalTime',
    timeColumnLabel = 'Arrival Time',
    locationColumnKey = 'pickupLocation',
    locationColumnLabel = 'Pickup Location',
    includeNoData = false,
    onOpenOverrideModal,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const filteredData = includeNoData ? (rawData || []) : (rawData || []).filter((row) => !isNoDataRow(row));
    const showActionColumn = !!onOpenOverrideModal;
    const columnWidths = showActionColumn ? columnWidthsWithAction : columnWidthsWithoutAction;

    if (!filteredData?.length) {
        return (
            <div>
                <p style={subTitleStyle}>{title} ({minutes} min) {date ? `(${date})` : ''}</p>
                <p style={{ fontSize: '13px', color: '#9ca3af' }}>No records.</p>
            </div>
        );
    }

    const containerStyle = {
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        padding: '12px',
        marginBottom: '12px',
        backgroundColor: '#fff',
    };

    return (
        <div style={containerStyle}>
            <div
                onClick={() => setIsOpen(!isOpen)}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    cursor: 'pointer',
                    userSelect: 'none',
                }}
            >
                <span style={{
                    display: 'inline-block',
                    fontSize: '12px',
                    color: '#9ca3af',
                    transition: 'transform 0.2s ease',
                    transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                }}>
                    &#9654;
                </span>
                <p style={{ ...subTitleStyle, marginTop: 0, marginBottom: 0 }}>{title} ({minutes} min) ({filteredData.length}) {date ? `(${date})` : ''}</p>
            </div>

            {!isOpen ? null : (
                <>
                    <table style={{ ...tableStyle, borderBottom: 'none', marginTop: '10px' }}>
                        <colgroup>
                            {columnWidths.map((width, i) => (
                                <col key={i} style={{ width }} />
                            ))}
                        </colgroup>
                        <thead>
                            <tr>
                                <th style={headerStyle}>Pax Name</th>
                                <th style={headerStyle}>Passenger ID</th>
                                <th style={headerStyle}>Flight</th>
                                <th style={headerStyle}>{timeColumnLabel}</th>
                                <th style={headerStyle}>{locationColumnLabel}</th>
                                <th style={headerStyle}>Start Waiting Time</th>
                                <th style={headerStyle}>Agent At Location</th>
                                <th style={{ ...headerStyle, textAlign: 'center' }}>Waiting Time</th>
                                <th style={{ ...headerStyle, textAlign: 'center' }}>Pass</th>
                                <th style={headerStyle}>Reason</th>
                                <th style={headerStyle}>Notes</th>
                                {showActionColumn && <th style={{ ...headerStyle, textAlign: 'center' }}>Action</th>}
                            </tr>
                        </thead>
                    </table>
                    <div style={{ maxHeight: '400px', overflowY: 'auto', borderTop: 'none' }}>
                        <table style={tableStyle}>
                            <colgroup>
                                {columnWidths.map((width, i) => (
                                    <col key={i} style={{ width }} />
                                ))}
                            </colgroup>
                            <tbody>
                                {filteredData.map((row, idx) => (
                                    <tr key={row.bookingId || `${row.passengerName}-${idx}`}>
                                        <td style={cellStyle}>{row.passengerName ?? '-'}</td>
                                        <td style={{ ...cellStyle, fontSize: '11px', wordBreak: 'break-all' }}>{row.passengerId ?? '–'}</td>
                                        <td style={cellStyle}>{row.flightNumber ?? '-'}</td>
                                        <td style={cellStyle}>{formatTime(row[timeColumnKey])}</td>
                                        <td style={cellStyle}>{row[locationColumnKey] ?? '-'}</td>
                                        <td style={cellStyle}>{formatTime(row.startWaitingTime)}</td>
                                        <td style={cellStyle}>{formatTime(row.atLocationTime)}</td>
                                        <td style={{ ...cellStyle, textAlign: 'center' }}>
                                            {row.responseMinutes != null ? row.responseMinutes.toFixed(1) : '-'}
                                        </td>
                                        <td style={{
                                            ...cellStyle,
                                            textAlign: 'center',
                                            backgroundColor: row.passReason === 'No data' || row.passReason === 'Invalid times' ? '#f3f4f6' : row.pass ? '#dcfce7' : '#fee2e2',
                                            color: row.passReason === 'No data' || row.passReason === 'Invalid times' ? '#6b7280' : row.pass ? '#166534' : '#991b1b',
                                        }}>
                                            {row.passReason === 'No data' || row.passReason === 'Invalid times' ? 'No data' : row.pass ? 'Pass' : 'Fail'}
                                        </td>
                                        <td style={cellStyle}>{row.passReason ?? '-'}</td>
                                        <td style={{ ...cellStyle, fontSize: '12px', color: '#6b7280' }}>{row.notes ?? '-'}</td>
                                        {showActionColumn && (
                                            <td style={{ ...cellStyle, textAlign: 'center' }}>
                                                {row.bookingId ? (
                                                    <button
                                                        type="button"
                                                        onClick={() => onOpenOverrideModal({ ...row, kpiKey })}
                                                        style={{
                                                            padding: '6px 12px',
                                                            fontSize: '12px',
                                                            cursor: 'pointer',
                                                            backgroundColor: '#3b82f6',
                                                            color: '#fff',
                                                            border: 'none',
                                                            borderRadius: '6px',
                                                        }}
                                                    >
                                                        Action
                                                    </button>
                                                ) : '-'}
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
};

const TerminalReceptionRawDataTable = ({
    dailyReports,
    timeColumnKey = 'arrivalTime',
    timeColumnLabel = 'Arrival Time',
    locationColumnKey = 'pickupLocation',
    locationColumnLabel = 'Pickup Location',
    includeNoData = false,
    overrideRow,
    onOverrideRowChange,
    onPatchBooking,
    isPatching,
}) => {
    if (!dailyReports?.length) return null;

    const handleOpenOverrideModal = (row) => onOverrideRowChange?.(row);
    const handleApplyOverride = (row, pass, reason) => {
        if (row?.bookingId && onPatchBooking) onPatchBooking(row, pass, reason, row.kpiKey);
    };

    return (
        <div style={{ marginTop: '32px' }}>
            {dailyReports.map((report) => {
                const kpiKeys = report.kpis ? Object.keys(report.kpis) : [];

                return (
                    <div key={report.date} style={{ marginBottom: '24px' }}>
                        {kpiKeys.map((key) => {
                            const kpi = report.kpis[key];
                            return (
                                <KpiRawDataSection
                                    key={key}
                                    kpiKey={key}
                                    title={kpi.title}
                                    minutes={kpi.minutes}
                                    rawData={kpi.rawData}
                                    date={report.date}
                                    timeColumnKey={timeColumnKey}
                                    timeColumnLabel={timeColumnLabel}
                                    locationColumnKey={locationColumnKey}
                                    locationColumnLabel={locationColumnLabel}
                                    includeNoData={includeNoData}
                                    onOpenOverrideModal={onOverrideRowChange ? handleOpenOverrideModal : undefined}
                                />
                            );
                        })}
                    </div>
                );
            })}
            <PassFailOverrideModal
                row={overrideRow}
                open={!!overrideRow}
                onClose={() => onOverrideRowChange?.(null)}
                onApply={handleApplyOverride}
                isPatching={isPatching}
            />
        </div>
    );
};

export default TerminalReceptionRawDataTable;
